import React from 'react';
import {
  Box, Grid, Button, Divider, CircularProgress, Typography,
  IconButton, TextField, FormControl, InputLabel, Select, MenuItem, Autocomplete,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { postRequest, getRequest } from 'Includes/api/apicall';
import { POST_URL, GET_URL } from 'Includes/urls';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer_unaccountable', label: 'Bank Transfer (Unaccountable)' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'gateway', label: 'Payment Gateway' },
];

const ADD_NEW_TYPE = '__add_new__';
const ADD_NEW_RECEIVER = '__add_new_receiver__';

function slugify(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9_-]/g, '')
    .slice(0, 64);
}

const emptyExpense = () => ({
  company_id: '',
  expense_type: '',
  expense_date: new Date().toISOString().split('T')[0],
  amount: '',
  quantity: 1,
  reciever: '',
  mop: 'upi',
  reference: '',
  notes: '',
  expense_receipt_file: null,
});

function ExpenseAdd(props) {
  const [loading, setLoading] = React.useState(false);
  const [submitDisable, setSubmitDisable] = React.useState(false);
  const [expenseTypes, setExpenseTypes] = React.useState([]);
  const [receivers, setReceivers] = React.useState([]);
  const [companies, setCompanies] = React.useState([]);
  const [companySearchText, setCompanySearchText] = React.useState('');
  const [companiesLoading, setCompaniesLoading] = React.useState(false);
  const [typeDialogOpen, setTypeDialogOpen] = React.useState(false);
  const [typeDialogForId, setTypeDialogForId] = React.useState(null);
  const [newTypeName, setNewTypeName] = React.useState('');
  const [newTypeDescription, setNewTypeDescription] = React.useState('');
  const [savingType, setSavingType] = React.useState(false);
  const [receiverDialogOpen, setReceiverDialogOpen] = React.useState(false);
  const [receiverDialogForId, setReceiverDialogForId] = React.useState(null);
  const [newReceiverName, setNewReceiverName] = React.useState('');
  const [savingReceiver, setSavingReceiver] = React.useState(false);
  const navigate = useNavigate();
  const isSuperuser = React.useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return Boolean(u?.is_superuser);
    } catch {
      return false;
    }
  }, []);

  const [expenses, setExpenses] = React.useState([{ id: 0 }]);
  const [expenseDetails, setExpenseDetails] = React.useState({ 0: emptyExpense() });
  const [expenseErrors, setExpenseErrors] = React.useState({});
  const sectionTitleSx = {
    mb: 2.5,
    pl: 1.75,
    borderLeft: '4px solid #4680FF',
    color: '#4680FF',
    fontWeight: 600,
    fontSize: { xs: '1rem', sm: '1.05rem' },
    lineHeight: 1.35,
  };
  const sectionPaperSx = {
    p: { xs: 2, sm: 2.5, md: 3 },
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
  };

  React.useEffect(() => {
    if (!isSuperuser) {
      Swal.fire({
        icon: 'error',
        title: 'Access Denied',
        text: 'You do not have permission to access this page. Superuser access required.',
        confirmButtonText: 'OK'
      }).then(() => {
        navigate('/dashboard');
      });
    }
  }, [isSuperuser, navigate]);

  const fetchExpenseTypes = React.useCallback(() => {
    return getRequest(GET_URL.expense_types.api, {}, props).then(response => {
      if (response && response.status === 200) {
        const data = response.data.data || response.data || [];
        setExpenseTypes(Array.isArray(data) ? data : []);
        return data;
      }
      return [];
    }).catch(() => []);
  }, [props]);

  const fetchReceivers = React.useCallback(() => {
    return getRequest(GET_URL.expense_receivers.api, {}, props).then(response => {
      if (response && response.status === 200) {
        const data = response.data.data || response.data || [];
        setReceivers(Array.isArray(data) ? data : []);
        return data;
      }
      return [];
    }).catch(() => []);
  }, [props]);

  React.useEffect(() => {
    if (!isSuperuser) return;
    fetchExpenseTypes();
    fetchReceivers();
  }, [isSuperuser, fetchExpenseTypes, fetchReceivers]);

  React.useEffect(() => {
    if (!isSuperuser) return;
    const timer = setTimeout(() => {
      setCompaniesLoading(true);
      getRequest(
        GET_URL.company.api,
        { limit: 10, pageno: 1, search: (companySearchText || '').trim() },
        props
      )
        .then((response) => {
          if (response && response.status === 200) {
            setCompanies(response.data?.data || []);
          } else {
            setCompanies([]);
          }
        })
        .catch(() => setCompanies([]))
        .finally(() => setCompaniesLoading(false));
    }, 300);

    return () => clearTimeout(timer);
  }, [isSuperuser, companySearchText, props]);

  const addExpense = () => {
    const newId = expenses.length > 0 ? Math.max(...expenses.map(e => e.id)) + 1 : 0;
    setExpenses([...expenses, { id: newId }]);
    setExpenseDetails(prev => ({
      ...prev,
      [newId]: emptyExpense(),
    }));
  };

  const removeExpense = (expenseId) => {
    if (expenses.length > 1) {
      setExpenses(expenses.filter(e => e.id !== expenseId));
      const newExpenseDetails = { ...expenseDetails };
      delete newExpenseDetails[expenseId];
      setExpenseDetails(newExpenseDetails);

      const newErrors = { ...expenseErrors };
      Object.keys(newErrors).forEach(key => {
        if (key.startsWith(`${expenseId}_`)) {
          delete newErrors[key];
        }
      });
      setExpenseErrors(newErrors);
    }
  };

  const handleFieldChange = (expenseId, fieldName, value) => {
    setExpenseDetails(prev => ({
      ...prev,
      [expenseId]: {
        ...prev[expenseId],
        [fieldName]: value
      }
    }));

    setExpenseErrors(prev => ({
      ...prev,
      [`${expenseId}_${fieldName}`]: ''
    }));
  };

  const handleFileChange = (expenseId, file) => {
    setExpenseDetails(prev => ({
      ...prev,
      [expenseId]: {
        ...prev[expenseId],
        expense_receipt_file: file
      }
    }));
  };

  const validate = () => {
    const errors = {};
    let isValid = true;

    expenses.forEach((expense) => {
      const data = expenseDetails[expense.id];

      if (!data.expense_type || data.expense_type === '') {
        errors[`${expense.id}_expense_type`] = 'Expense Type is required';
        isValid = false;
      }

      if (!data.expense_date) {
        errors[`${expense.id}_expense_date`] = 'Expense Date is required';
        isValid = false;
      }

      if (!data.amount || data.amount.toString().trim() === '' || parseFloat(data.amount) <= 0) {
        errors[`${expense.id}_amount`] = 'Amount is required and must be greater than 0';
        isValid = false;
      }

      if (!data.quantity || data.quantity.toString().trim() === '' || parseInt(data.quantity, 10) <= 0) {
        errors[`${expense.id}_quantity`] = 'Quantity is required and must be greater than 0';
        isValid = false;
      }

      if (!data.reciever || data.reciever.trim() === '') {
        errors[`${expense.id}_reciever`] = 'Receiver is required';
        isValid = false;
      }

      if (!data.mop || data.mop === '') {
        errors[`${expense.id}_mop`] = 'Payment Method is required';
        isValid = false;
      }
    });

    setExpenseErrors(errors);
    return isValid;
  };

  const submit = () => {
    if (validate()) {
      setSubmitDisable(true);

      const formData = new FormData();

      const expensesData = expenses.map((expense) => {
        const data = expenseDetails[expense.id];
        return {
          ...(data.company_id ? { company_id: Number(data.company_id) } : {}),
          expense_type: data.expense_type,
          expense_date: data.expense_date,
          amount: parseFloat(data.amount),
          quantity: parseInt(data.quantity, 10),
          reciever: data.reciever.trim(),
          mop: data.mop,
          reference: (data.reference || '').trim(),
          notes: (data.notes || '').trim(),
        };
      });

      formData.append('expenses', JSON.stringify(expensesData));

      expenses.forEach((expense, index) => {
        const data = expenseDetails[expense.id];
        if (data.expense_receipt_file) {
          formData.append(`expense_receipt_${index}`, data.expense_receipt_file);
        }
      });

      const url = POST_URL.expenses.api;
      postRequest(url, formData, props).then(response => {
        setSubmitDisable(false);
        if (response && response.status === 200) {
          Swal.fire({
            position: 'top-end',
            type: 'success',
            title: response.data.Reason || 'Success',
            text: 'Expense(s) added successfully',
            showConfirmButton: false,
            timer: 1500
          });
          navigate('/expenses/expense/list');
        }
      }).catch((error) => {
        setSubmitDisable(false);
        const errorMessage = error?.response?.data?.Reason || error?.response?.data?.name?.[0] || 'Failed to create expense(s)';
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: errorMessage,
          timer: 2000,
          showConfirmButton: false
        });
      });
    }
  };

  const resetForm = () => {
    setExpenses([{ id: 0 }]);
    setExpenseDetails({ 0: emptyExpense() });
    setExpenseErrors({});
  };

  const openTypeDialog = (expenseId) => {
    setTypeDialogForId(expenseId);
    setNewTypeName('');
    setNewTypeDescription('');
    setTypeDialogOpen(true);
  };

  const saveExpenseType = () => {
    const name = (newTypeName || '').trim();
    if (!name) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Expense type name is required', timer: 1500, showConfirmButton: false });
      return;
    }
    const code = slugify(name);
    if (!code) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Could not generate a valid code from the name', timer: 1500, showConfirmButton: false });
      return;
    }
    setSavingType(true);
    postRequest(POST_URL.expense_types.api, [{
      code,
      name,
      description: (newTypeDescription || '').trim(),
    }], props)
      .then((response) => {
        if (response && response.status === 200) {
          return fetchExpenseTypes().then((types) => {
            const created = (Array.isArray(types) ? types : []).find((t) => t.code === code || t.name === name);
            if (created && typeDialogForId !== null) {
              handleFieldChange(typeDialogForId, 'expense_type', created.id);
            }
            setTypeDialogOpen(false);
            setNewTypeName('');
            setNewTypeDescription('');
          });
        }
      })
      .catch((error) => {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error?.response?.data?.Reason || error?.response?.data?.code?.[0] || 'Failed to add expense type',
          timer: 2000,
          showConfirmButton: false,
        });
      })
      .finally(() => setSavingType(false));
  };

  const openReceiverDialog = (expenseId) => {
    setReceiverDialogForId(expenseId);
    setNewReceiverName('');
    setReceiverDialogOpen(true);
  };

  const saveReceiver = () => {
    const name = (newReceiverName || '').trim();
    if (!name) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Receiver name is required', timer: 1500, showConfirmButton: false });
      return;
    }
    setSavingReceiver(true);
    postRequest(POST_URL.expense_receivers.api, { name }, props)
      .then((response) => {
        if (response && response.status === 200) {
          const createdName = response.data?.data?.name || name;
          return fetchReceivers().then(() => {
            if (receiverDialogForId !== null) {
              handleFieldChange(receiverDialogForId, 'reciever', createdName);
            }
            setReceiverDialogOpen(false);
            setNewReceiverName('');
          });
        }
      })
      .catch((error) => {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error?.response?.data?.Reason || error?.response?.data?.name?.[0] || 'Failed to add receiver',
          timer: 2000,
          showConfirmButton: false,
        });
      })
      .finally(() => setSavingReceiver(false));
  };

  if (!isSuperuser) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6" color="error">
          Access Denied. Superuser access required.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={2}>
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      )}
      {!loading && (
        <Box>
          <Grid container alignItems="center" spacing={2}>
            <Grid item md={6} xs={12} mt={2}>
              <Typography variant="h5" fontWeight={600}>
                Expense Informations
              </Typography>
            </Grid>
            <Grid item md={6} xs={12}>
              <Box display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} alignItems="center" gap={1} flexWrap="wrap">
                <Button
                  variant="contained"
                  onClick={() => navigate('/expenses/expense/list')}
                  sx={{ textTransform: 'none' }}
                >
                  <ArrowBackOutlinedIcon sx={{ mr: 1 }} />
                  View Expenses
                </Button>
                <IconButton color="primary" onClick={addExpense} size="small" title="Add another expense">
                  <AddIcon />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
          <Box mt={3} mb={3}><Divider /></Box>

          <Box sx={sectionPaperSx}>
            <Typography variant="subtitle1" component="h2" sx={sectionTitleSx}>
              Expense Details
            </Typography>
          {expenses.map((expense, index) => (
            <Box key={expense.id} mb={3} p={2} border={1} borderColor="grey.300" borderRadius={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#1976d2' }}>
                  Expense {index + 1}
                </Typography>
                {expenses.length > 1 && (
                  <IconButton
                    color="secondary"
                    onClick={() => removeExpense(expense.id)}
                    size="small"
                    title="Remove this expense"
                  >
                    <RemoveIcon />
                  </IconButton>
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Autocomplete
                    options={companies || []}
                    getOptionLabel={(option) => option?.name || ''}
                    value={(companies || []).find((c) => Number(c.id) === Number(expenseDetails[expense.id]?.company_id)) || null}
                    onChange={(_, selected) => handleFieldChange(expense.id, 'company_id', selected?.id || '')}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onInputChange={(_, value) => setCompanySearchText(value || '')}
                    loading={companiesLoading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Company (Optional)"
                        variant="outlined"
                        fullWidth
                        placeholder="Search company"
                      />
                    )}
                    clearOnEscape
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth required error={!!expenseErrors[`${expense.id}_expense_type`]}>
                    <InputLabel>Expense Type</InputLabel>
                    <Select
                      value={expenseDetails[expense.id]?.expense_type || ''}
                      onChange={(e) => {
                        if (e.target.value === ADD_NEW_TYPE) {
                          openTypeDialog(expense.id);
                          return;
                        }
                        handleFieldChange(expense.id, 'expense_type', e.target.value);
                      }}
                      label="Expense Type"
                    >
                      {expenseTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                      <MenuItem value={ADD_NEW_TYPE}>Add New</MenuItem>
                    </Select>
                    {expenseErrors[`${expense.id}_expense_type`] && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {expenseErrors[`${expense.id}_expense_type`]}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth required error={!!expenseErrors[`${expense.id}_reciever`]}>
                    <InputLabel>Receiver</InputLabel>
                    <Select
                      value={expenseDetails[expense.id]?.reciever || ''}
                      onChange={(e) => {
                        if (e.target.value === ADD_NEW_RECEIVER) {
                          openReceiverDialog(expense.id);
                          return;
                        }
                        handleFieldChange(expense.id, 'reciever', e.target.value);
                      }}
                      label="Receiver"
                    >
                      {receivers.map((receiver) => (
                        <MenuItem key={receiver.id} value={receiver.name}>
                          {receiver.name}
                        </MenuItem>
                      ))}
                      <MenuItem value={ADD_NEW_RECEIVER}>Add New</MenuItem>
                    </Select>
                    {expenseErrors[`${expense.id}_reciever`] && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {expenseErrors[`${expense.id}_reciever`]}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Expense Date"
                    type="date"
                    variant="outlined"
                    fullWidth
                    required
                    value={expenseDetails[expense.id]?.expense_date || ''}
                    onChange={(e) => handleFieldChange(expense.id, 'expense_date', e.target.value)}
                    error={!!expenseErrors[`${expense.id}_expense_date`]}
                    helperText={expenseErrors[`${expense.id}_expense_date`] || ''}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Amount"
                    type="number"
                    variant="outlined"
                    fullWidth
                    required
                    value={expenseDetails[expense.id]?.amount || ''}
                    onChange={(e) => handleFieldChange(expense.id, 'amount', e.target.value)}
                    error={!!expenseErrors[`${expense.id}_amount`]}
                    helperText={expenseErrors[`${expense.id}_amount`] || ''}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Quantity"
                    type="number"
                    variant="outlined"
                    fullWidth
                    required
                    value={expenseDetails[expense.id]?.quantity ?? ''}
                    onChange={(e) => handleFieldChange(expense.id, 'quantity', e.target.value)}
                    error={!!expenseErrors[`${expense.id}_quantity`]}
                    helperText={expenseErrors[`${expense.id}_quantity`] || ''}
                    inputProps={{ min: 1, step: 1 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth required error={!!expenseErrors[`${expense.id}_mop`]}>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={expenseDetails[expense.id]?.mop || 'upi'}
                      onChange={(e) => handleFieldChange(expense.id, 'mop', e.target.value)}
                      label="Payment Method"
                    >
                      {PAYMENT_METHODS.map((method) => (
                        <MenuItem key={method.value} value={method.value}>
                          {method.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {expenseErrors[`${expense.id}_mop`] && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {expenseErrors[`${expense.id}_mop`]}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Reference"
                    variant="outlined"
                    fullWidth
                    value={expenseDetails[expense.id]?.reference || ''}
                    onChange={(e) => handleFieldChange(expense.id, 'reference', e.target.value)}
                    inputProps={{ maxLength: 255 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="file"
                    variant="outlined"
                    fullWidth
                    label="Expense Receipt"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ accept: 'image/*,.pdf' }}
                    onChange={(e) => handleFileChange(expense.id, e.target.files[0] || null)}
                  />
                  {expenseDetails[expense.id]?.expense_receipt_file && (
                    <Typography variant="caption" color="textSecondary" style={{ marginTop: 4 }}>
                      Selected: {expenseDetails[expense.id].expense_receipt_file.name}
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={2}
                    value={expenseDetails[expense.id]?.notes || ''}
                    onChange={(e) => handleFieldChange(expense.id, 'notes', e.target.value)}
                  />
                </Grid>
              </Grid>
            </Box>
          ))}

            <Box mt={3} mb={3}>
              <Divider />
            </Box>
            <Box display='flex' justifyContent='flex-end' gap={2} className='mv-20'>
            <Button
              variant='outlined'
              onClick={resetForm}
            >
              Reset
            </Button>
            <Button
              variant='contained'
              color='primary'
              className='submit'
              disabled={submitDisable}
              onClick={submit}
            >
              {submitDisable ? 'Submitting...' : 'Submit'}
            </Button>
            </Box>
          </Box>
        </Box>
      )}

      <Dialog open={typeDialogOpen} onClose={() => setTypeDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Expense Type</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            required
            value={newTypeName}
            onChange={(e) => setNewTypeName(e.target.value)}
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={newTypeDescription}
            onChange={(e) => setNewTypeDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTypeDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={saveExpenseType} disabled={savingType} sx={{ textTransform: 'none' }}>
            {savingType ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={receiverDialogOpen} onClose={() => setReceiverDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Receiver</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            required
            value={newReceiverName}
            onChange={(e) => setNewReceiverName(e.target.value)}
            inputProps={{ maxLength: 255 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReceiverDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={saveReceiver} disabled={savingReceiver} sx={{ textTransform: 'none' }}>
            {savingReceiver ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ExpenseAdd;
